import React, { useEffect, useState } from 'react';
import { isAssetUrl, resolveAssetUrl } from '../../utils/assetUrl';

export const DESK_SIZE = 500;

const photoTrimCache = new Map();

const colorDistance = (left, right) => (
  Math.abs(left.r - right.r)
  + Math.abs(left.g - right.g)
  + Math.abs(left.b - right.b)
);

const getPixelColor = (data, index) => ({
  r: data[index],
  g: data[index + 1],
  b: data[index + 2],
  a: data[index + 3],
});

const getAverageCornerColor = (data, width, height) => {
  const sampleSize = Math.max(2, Math.min(12, Math.floor(Math.min(width, height) * 0.04)));
  const points = [
    [0, 0],
    [width - sampleSize, 0],
    [0, height - sampleSize],
    [width - sampleSize, height - sampleSize],
  ];
  const total = { r: 0, g: 0, b: 0, count: 0 };

  points.forEach(([startX, startY]) => {
    for (let y = startY; y < startY + sampleSize; y += 1) {
      for (let x = startX; x < startX + sampleSize; x += 1) {
        const color = getPixelColor(data, (y * width + x) * 4);
        if (color.a <= 12) continue;
        total.r += color.r;
        total.g += color.g;
        total.b += color.b;
        total.count += 1;
      }
    }
  });

  if (!total.count) return { r: 0, g: 0, b: 0 };

  return {
    r: total.r / total.count,
    g: total.g / total.count,
    b: total.b / total.count,
  };
};

const createEmptyBounds = (width, height) => ({
  minX: width,
  minY: height,
  maxX: -1,
  maxY: -1,
});

const expandBounds = (bounds, x, y) => {
  if (x < bounds.minX) bounds.minX = x;
  if (y < bounds.minY) bounds.minY = y;
  if (x > bounds.maxX) bounds.maxX = x;
  if (y > bounds.maxY) bounds.maxY = y;
};

const toTrimData = (bounds, image, center = null) => ({
  naturalWidth: image.naturalWidth,
  naturalHeight: image.naturalHeight,
  trimLeft: bounds.minX,
  trimTop: bounds.minY,
  trimWidth: Math.max(1, bounds.maxX - bounds.minX + 1),
  trimHeight: Math.max(1, bounds.maxY - bounds.minY + 1),
  contentCenterX: Number.isFinite(center?.x)
    ? center.x
    : (bounds.minX + (Math.max(1, bounds.maxX - bounds.minX + 1) / 2)),
  contentCenterY: Number.isFinite(center?.y)
    ? center.y
    : (bounds.minY + (Math.max(1, bounds.maxY - bounds.minY + 1) / 2)),
});

const isBackgroundLike = (color, backgroundColor) => (
  color.a <= 12 || colorDistance(color, backgroundColor) <= 72
);

const computeForegroundBounds = (data, width, height, backgroundColor) => {
  const visited = new Uint8Array(width * height);
  const queue = [];
  const enqueueIfBackground = (x, y) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;

    const pixelIndex = y * width + x;
    if (visited[pixelIndex]) return;

    const color = getPixelColor(data, pixelIndex * 4);
    if (!isBackgroundLike(color, backgroundColor)) return;

    visited[pixelIndex] = 1;
    queue.push(pixelIndex);
  };

  for (let x = 0; x < width; x += 1) {
    enqueueIfBackground(x, 0);
    enqueueIfBackground(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    enqueueIfBackground(0, y);
    enqueueIfBackground(width - 1, y);
  }

  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const pixelIndex = queue[cursor];
    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);

    enqueueIfBackground(x + 1, y);
    enqueueIfBackground(x - 1, y);
    enqueueIfBackground(x, y + 1);
    enqueueIfBackground(x, y - 1);
  }

  const bounds = createEmptyBounds(width, height);
  let pixels = 0;
  let sumX = 0;
  let sumY = 0;

  for (let pixelIndex = 0; pixelIndex < visited.length; pixelIndex += 1) {
    if (visited[pixelIndex]) continue;

    const color = getPixelColor(data, pixelIndex * 4);
    if (color.a <= 12) continue;

    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);
    expandBounds(bounds, x, y);
    sumX += x;
    sumY += y;
    pixels += 1;
  }

  return {
    bounds,
    pixels,
    center: pixels > 0
      ? { x: sumX / pixels, y: sumY / pixels }
      : null,
  };
};

const computeTrimBounds = (image) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0);

  const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
  const alphaBounds = createEmptyBounds(width, height);
  const backgroundColor = getAverageCornerColor(data, width, height);
  let alphaPixels = 0;
  let alphaSumX = 0;
  let alphaSumY = 0;

  for (let index = 3; index < data.length; index += 4) {
    if (data[index] <= 12) continue;

    const pixelIndex = (index - 3) / 4;
    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);

    expandBounds(alphaBounds, x, y);
    alphaSumX += x;
    alphaSumY += y;
    alphaPixels += 1;
  }

  if (alphaBounds.maxX === -1 || alphaBounds.maxY === -1) {
    return {
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      trimLeft: 0,
      trimTop: 0,
      trimWidth: image.naturalWidth,
      trimHeight: image.naturalHeight,
    };
  }

  const alphaCenter = alphaPixels > 0
    ? { x: alphaSumX / alphaPixels, y: alphaSumY / alphaPixels }
    : null;
  const {
    bounds: foregroundBounds,
    pixels: foregroundPixels,
    center: foregroundCenter,
  } = computeForegroundBounds(data, width, height, backgroundColor);
  const alphaArea = (alphaBounds.maxX - alphaBounds.minX + 1) * (alphaBounds.maxY - alphaBounds.minY + 1);
  const foregroundArea = foregroundBounds.maxX === -1
    ? 0
    : (foregroundBounds.maxX - foregroundBounds.minX + 1) * (foregroundBounds.maxY - foregroundBounds.minY + 1);
  const foregroundAreaCoverage = foregroundArea / Math.max(alphaArea, 1);
  const foregroundPixelCoverage = foregroundPixels / Math.max(alphaPixels, 1);
  const hasUsefulForegroundTrim = (
    foregroundPixels > width * height * 0.005
    && foregroundArea < alphaArea * 0.96
    && foregroundAreaCoverage >= 0.08
    && foregroundPixelCoverage >= 0.08
  );

  return toTrimData(
    hasUsefulForegroundTrim ? foregroundBounds : alphaBounds,
    image,
    hasUsefulForegroundTrim ? foregroundCenter : alphaCenter
  );
};

// eslint-disable-next-line no-unused-vars
const TrimmedPhotoImage = ({ src, alt, boxWidth, boxHeight }) => {
  const cacheKey = `v2:${src}`;
  const [trimData, setTrimData] = useState(() => photoTrimCache.get(cacheKey) || null);

  useEffect(() => {
    if (!src || trimData || typeof window === 'undefined') return undefined;

    let isActive = true;
    const image = new window.Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      if (!isActive) return;

      try {
        const nextTrimData = computeTrimBounds(image);
        photoTrimCache.set(cacheKey, nextTrimData);
        setTrimData(nextTrimData);
      } catch (error) {
        const fallbackTrimData = {
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
          trimLeft: 0,
          trimTop: 0,
          trimWidth: image.naturalWidth,
          trimHeight: image.naturalHeight,
        };
        photoTrimCache.set(cacheKey, fallbackTrimData);
        setTrimData(fallbackTrimData);
      }
    };

    image.onerror = () => {
      if (!isActive) return;
      setTrimData(null);
    };

    image.src = src;

    return () => {
      isActive = false;
    };
  }, [cacheKey, src, trimData]);

  if (!trimData || !boxWidth || !boxHeight) {
    return (
      <img
        src={src}
        alt={alt}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  }

  const scale = Math.min(boxWidth / trimData.trimWidth, boxHeight / trimData.trimHeight);
  const centerX = trimData.contentCenterX ?? (trimData.trimLeft + trimData.trimWidth / 2);
  const centerY = trimData.contentCenterY ?? (trimData.trimTop + trimData.trimHeight / 2);
  const offsetX = (boxWidth / 2) - centerX * scale;
  const offsetY = (boxHeight / 2) - centerY * scale;

  return (
    <span style={{ position: 'relative', display: 'block', width: '100%', height: '100%', overflow: 'hidden' }}>
      <img
        src={src}
        alt={alt}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${trimData.naturalWidth}px`,
          height: `${trimData.naturalHeight}px`,
          transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
          transformOrigin: 'top left',
          display: 'block',
        }}
      />
    </span>
  );
};

const PhotoItemImage = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          lineHeight: 1,
        }}
      >
        🍽️
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  );
};

export const resolveIcon = (item, dishes) => {
  if (item.icon) return item.icon;
  return dishes.find((dish) => String(dish._id || dish.id) === String(item.type))?.icon || '🍽️';
};

export const getItemProfile = (item) => {
  if (item.category === 'coloring' || String(item.type || '').startsWith('coloring:')) return 'coloring';

  const text = `${item.name || ''} ${item.type || ''}`.toLowerCase();

  if (/cup|mug|чаш|круж|coffee|tea/.test(text)) return 'cup';
  if (/plate|dish|таріл|блюд|saucer/.test(text)) return 'flat';
  if (/glass|wine|champagne|келих|склян/.test(text)) return 'glass';
  if (/fork|knife|spoon|ложк|видел|вилк|ніж/.test(text)) return 'utensil';

  return 'default';
};

const buildItemStyle = (item, options = {}) => {
  const depthScale = options.disableDepthScale
    ? 1
    : 1 + ((item.y || 0) / DESK_SIZE) * 0.12;
  const layerBase = options.layerBase ?? 20;
  const itemHeight = `${item.height ?? 40}px`;

  const style = {
    left: `${((item.x || 0) / DESK_SIZE) * 100}%`,
    top: `${((item.y || 0) / DESK_SIZE) * 100}%`,
    width: `${item.width ?? 40}px`,
    transform: `translate(-50%, -50%) rotate(${item.rotation || 0}deg) scale(${depthScale})`,
    zIndex: layerBase + (item.zIndex ?? Math.round(item.y || 0)),
  };

  if (options.fixedHeight) {
    style.height = itemHeight;
  } else {
    style.minHeight = itemHeight;
  }

  return style;
};

const compareByZIndex = (left, right) => (
  (left.zIndex ?? Math.round(left.y || 0)) - (right.zIndex ?? Math.round(right.y || 0))
);

const getTopmostLayerAtPoint = (event) => {
  if (typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') {
    return null;
  }

  const layersAtPoint = document.elementsFromPoint(event.clientX, event.clientY);
  for (const element of layersAtPoint) {
    if (!(element instanceof HTMLElement)) continue;

    const layerNode = element.closest('[data-layer-kind][data-layer-id]');
    if (layerNode) {
      return {
        kind: layerNode.dataset.layerKind,
        id: layerNode.dataset.layerId,
      };
    }
  }

  return null;
};

const isTopmostLayerTarget = (event, kind, id) => {
  const topmostLayer = getTopmostLayerAtPoint(event);
  if (!topmostLayer) return true;

  return topmostLayer.kind === kind && topmostLayer.id === String(id);
};

const DeskScene = ({
  deskRef,
  items,
  underlays = [],
  dishes,
  ghostItems = [],
  surfacePreset = 'transparent',
  surfaceColor = '#ffffff',
  selectedItemId = null,
  activeItemId = null,
  draggingItemId = null,
  emptyIcon = '+',
  emptyLabel = 'Натисніть для розміщення',
  onDeskClick,
  onItemClick,
  onItemPointerDown,
  onResizePointerDown,
  onRotateItem,
  onDeleteItem,
  showPlaceholder = true,
  showTransformControls = false,
  showDeleteControl = false,
  selectedUnderlayId = null,
  activeUnderlayId = null,
  onUnderlayClick,
  onUnderlayPointerDown,
  onUnderlayResizePointerDown,
  onDeleteUnderlay,
  showUnderlayControls = false,
  allowDeskClickThroughItems = false,
  forceItemsAboveUnderlays = false,
}) => {
  const sortedUnderlays = [...underlays].sort(compareByZIndex);
  const sortedItems = [...items].sort(compareByZIndex);
  const underlayLayerBase = forceItemsAboveUnderlays ? 0 : 20;
  const itemLayerBase = forceItemsAboveUnderlays ? 200 : 20;
  const ghostLayerBase = forceItemsAboveUnderlays ? 180 : 20;

  return (
    <div className="desk-workspace">
    <div className="desk-stage">
      <div className="desk-stage-shadow" aria-hidden="true" />
      <div className="desk-stage-base" aria-hidden="true" />
      <div
        ref={deskRef}
        className={`square-desk isometric-desk desk-surface-${surfacePreset}`}
        style={surfacePreset === 'transparent' ? undefined : { background: surfaceColor }}
        onClick={onDeskClick}
      >
        <div className="desk-grid" aria-hidden="true" />
        <div className="desk-guide desk-guide-horizontal" aria-hidden="true" />
        <div className="desk-guide desk-guide-vertical" aria-hidden="true" />

        {sortedUnderlays.map((underlay) => (
          <div
            key={`underlay-${underlay.id}`}
            data-layer-kind="underlay"
            data-layer-id={underlay.id}
            className={`desk-underlay ${activeUnderlayId === underlay.id ? 'dragging' : ''} ${selectedUnderlayId === underlay.id ? 'selected' : ''}`}
            style={buildItemStyle(underlay, { disableDepthScale: true, layerBase: underlayLayerBase, fixedHeight: true })}
            onClick={onUnderlayClick ? ((event) => {
              event.stopPropagation();
              if (!isTopmostLayerTarget(event, 'underlay', underlay.id)) return;
              onUnderlayClick(event, underlay);
            }) : undefined}
            onPointerDown={onUnderlayPointerDown ? ((event) => {
              if (!isTopmostLayerTarget(event, 'underlay', underlay.id)) return;
              onUnderlayPointerDown(event, underlay);
            }) : undefined}
          >
            <div className="desk-underlay-content">
              <img
                src={resolveAssetUrl(underlay.image)}
                alt={underlay.name || 'underlay'}
                className="desk-underlay-image"
              />
            </div>
            {showUnderlayControls && (
              <>
                <div className="desk-item-controls" onPointerDown={(event) => event.stopPropagation()}>
                  <button
                    className="item-delete"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => onDeleteUnderlay?.(event, underlay)}
                  >
                    x
                  </button>
                </div>
                {[
                  'n', 's', 'e', 'w',
                  'ne', 'nw', 'se', 'sw',
                ].map((direction) => (
                  <button
                    key={`${underlay.id}-${direction}`}
                    className={`underlay-resize-handle underlay-resize-${direction}`}
                    onPointerDown={(event) => onUnderlayResizePointerDown?.(event, underlay, direction)}
                    title="Змінити розмір"
                  />
                ))}
              </>
            )}
          </div>
        ))}

        {ghostItems.map((item, index) => {
          const itemIcon = resolveIcon(item, dishes);
          const isImage = isAssetUrl(itemIcon);

          return (
            <div
              key={`ghost-${item.id || item.type || index}`}
              className={`desk-item ghost-item ${isImage ? 'is-photo-item' : ''} item-profile-${getItemProfile(item)}`}
              style={buildItemStyle(item, { layerBase: ghostLayerBase, fixedHeight: isImage })}
            >
              <div className="desk-item-content" style={isImage ? undefined : { width: '100%', height: '100%', padding: 0 }}>
                <span 
                  className={`item-icon ${isImage ? 'item-icon-photo' : ''}`}
                  style={isImage ? undefined : {
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${Math.min(item.width ?? 84, item.height ?? 84) * 0.7}px`,
                    lineHeight: 1
                  }}
                >
                  {isImage ? (
                    <PhotoItemImage
                      src={resolveAssetUrl(itemIcon)}
                      alt={item.name || 'ghost-item'}
                    />
                  ) : (
                    itemIcon
                  )}
                </span>
                {!isImage && (
                  <span className="item-text" style={{ display: 'none' }}>{item.name}</span>
                )}
              </div>
            </div>
          );
        })}

        {sortedItems.map((item) => {
          const itemIcon = resolveIcon(item, dishes);
          const isImage = isAssetUrl(itemIcon);
          const showControls = showTransformControls || showDeleteControl;

          return (
            <div
              key={item._id || item.id}
              data-layer-kind="item"
              data-layer-id={item._id || item.id}
              data-layer-category={item.category || ''}
              data-layer-type={item.type || ''}
              className={`desk-item ${isImage ? 'is-photo-item' : ''} item-profile-${getItemProfile(item)} ${draggingItemId === (item._id || item.id) || activeItemId === (item._id || item.id) ? 'dragging' : ''} ${activeItemId === (item._id || item.id) || selectedItemId === (item._id || item.id) ? 'selected' : ''} ${item.isCorrect === true ? 'correct' : ''} ${item.isCorrect === false ? 'incorrect' : ''}`}
              style={buildItemStyle(item, { layerBase: itemLayerBase, fixedHeight: isImage })}
              onClick={allowDeskClickThroughItems
                ? onItemClick ? ((event) => {
                    event.stopPropagation();
                    if (!isTopmostLayerTarget(event, 'item', item._id || item.id)) return;
                    onItemClick(event, item);
                  }) : undefined
                : ((event) => {
                    event.stopPropagation();
                    if (!isTopmostLayerTarget(event, 'item', item._id || item.id)) return;
                    onItemClick?.(event, item);
                  })}
              onPointerDown={onItemPointerDown ? ((event) => {
                if (!isTopmostLayerTarget(event, 'item', item._id || item.id)) return;
                onItemPointerDown(event, item);
              }) : undefined}
            >
              <div className="desk-item-shadow" aria-hidden="true" />
              <div className="desk-item-content" style={isImage ? undefined : { width: '100%', height: '100%', padding: 0 }}>
                <span 
                  className={`item-icon ${isImage ? 'item-icon-photo' : ''}`}
                  style={isImage ? undefined : {
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${Math.min(item.width ?? 84, item.height ?? 84) * 0.7}px`,
                    lineHeight: 1
                  }}
                >
                  {isImage ? (
                    <PhotoItemImage
                      src={resolveAssetUrl(itemIcon)}
                      alt={item.name}
                    />
                  ) : (
                    itemIcon
                  )}
                </span>
                {!isImage && (
                  <span className="item-text" style={{ display: 'none' }}>{item.name}</span>
                )}
              </div>

              {showControls && (
                <div className="desk-item-controls" onPointerDown={(event) => event.stopPropagation()}>
                  {showTransformControls && (
                    <>
                      <button className="item-control" onClick={(event) => onRotateItem?.(event, item, -15)} title="Повернути ліворуч">⟲</button>
                      <button className="item-control" onClick={(event) => onRotateItem?.(event, item, 15)} title="Повернути праворуч">⟳</button>
                    </>
                  )}
                  {showDeleteControl && (
                    <button
                      className="item-delete"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => onDeleteItem?.(event, item)}
                    >
                      x
                    </button>
                  )}
                </div>
              )}

              {showTransformControls && onResizePointerDown && (
                <button
                  className="item-resize-handle"
                  onPointerDown={(event) => onResizePointerDown(event, item)}
                  title="Змінити розмір"
                />
              )}
            </div>
          );
        })}

        {items.length === 0 && showPlaceholder && (
          <div className="desk-placeholder">
            <span className="desk-icon">{emptyIcon}</span>
            <span className="desk-label">{emptyLabel}</span>
          </div>
        )}
      </div>

      <div className="desk-stage-front" aria-hidden="true">
        <div className="desk-stage-front-glow" />
      </div>
      <div className="desk-stage-leg desk-stage-leg-left" aria-hidden="true" />
      <div className="desk-stage-leg desk-stage-leg-right" aria-hidden="true" />
    </div>
  </div>
  );
};

export default DeskScene;
